import { ActionFunction, replace } from 'react-router-dom';
import { client } from '~/api/client';
import { parseVisibilityFormField, readOptionalFormString } from '~/api/data/form-data';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  UpdateCustomFormDocument,
  UpdateCustomFormMutation,
  UpdateCustomFormMutationVariables,
  UploadCustomFormDocument,
  UploadCustomFormMutation,
  UploadCustomFormMutationVariables,
  type VisibilityConfigInput
} from '~/api/graphql';

export const updateCustomForm = (variables: UpdateCustomFormMutationVariables) =>
  mutateData<UpdateCustomFormMutation, UpdateCustomFormMutationVariables>(client, {
    mutation: UpdateCustomFormDocument,
    variables
  });

export const uploadCustomForm = (variables: UploadCustomFormMutationVariables) =>
  mutateData<UploadCustomFormMutation, UploadCustomFormMutationVariables>(client, {
    mutation: UploadCustomFormDocument,
    variables
  });

// The schema has no bulk custom-form mutation, so fan out and collect rejected
// request IDs for retry.
export const archiveCustomForms = async ({ formIds }: { formIds: string[] }) => {
  const results = await Promise.all(
    (formIds ?? []).map(async (id) => {
      try {
        await client.mutate<UpdateCustomFormMutation, UpdateCustomFormMutationVariables>({
          mutation: UpdateCustomFormDocument,
          variables: { id, input: { archive: true } }
        });
        return { id, ok: true as const };
      } catch {
        return { id, ok: false as const };
      }
    })
  );
  return { failedIds: results.filter((r) => !r.ok).map((r) => r.id) };
};

export const updateCustomFormsVisibility = async ({
  formIds,
  visibility
}: {
  formIds: string[];
  visibility: VisibilityConfigInput;
}) => {
  const results = await Promise.all(
    (formIds ?? []).map(async (id) => {
      try {
        await client.mutate<UpdateCustomFormMutation, UpdateCustomFormMutationVariables>({
          mutation: UpdateCustomFormDocument,
          variables: { id, input: { visibility } }
        });
        return { id, ok: true as const };
      } catch {
        return { id, ok: false as const };
      }
    })
  );
  return { failedIds: results.filter((r) => !r.ok).map((r) => r.id) };
};

export const submitArchiveCustomForms: ActionFunction = async ({ request }) =>
  archiveCustomForms(await request.json());

export const submitUpdateCustomFormsVisibility: ActionFunction = async ({ request }) =>
  updateCustomFormsVisibility(await request.json());

export const submitUploadCustomForm: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  return submitMutation(
    uploadCustomForm({
      input: {
        title: formData.get('title') as string,
        file: formData.get('file') as File,
        ingestionGuidance: (formData.get('ingestionGuidance') as string) || undefined,
        visibility: parseVisibilityFormField(formData.get('visibility'))
      }
    })
  );
};

export const submitUpdateCustomForm: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  const file = formData.get('file');
  return submitMutation(
    updateCustomForm({
      id: params.id || '',
      input: {
        title: formData.get('title') as string,
        fillPrompt: (formData.get('fillPrompt') as string) || undefined,
        ingestionGuidance: readOptionalFormString(formData, 'ingestionGuidance'),
        file: file instanceof File ? file : undefined,
        visibility: parseVisibilityFormField(formData.get('visibility'))
      }
    }),
    async () => replace('/app/library/forms')
  );
};
